import type { FourPillars, PillarPosition } from '../types/index';
import type { DayMasterStrength, StrengthLevel } from '../types/index';
import type { TwelveStage } from '../types/index';
import type { Ohaeng } from '../types/index';
import type { Relation, RelationOptions } from '../types/relations';
import type { EarthlyBranch } from '../types/branches';
import { getStemElement, getElementRelation } from '../core/five-elements';
import { getHiddenStemsWithRatio } from '../core/hidden-stems';
import { getStemEumyang } from '../core/eumyang';
import { getTwelveStage } from './twelve-stages';

/** 辰戌丑未 (토 지지): 충 당해도 토 통근은 유지 */
const EARTH_BRANCHES: EarthlyBranch[] = ['辰', '戌', '丑', '未'];

/** 통근 약화 계수 */
const CLASH_PENALTY = 0.3;           // 충 → 통근 대폭 약화
const CLASH_PENALTY_EARTH = 0.5;     // 辰戌丑未 충 → 토 통근 부분 유지
const COMBINE_PENALTY_TRANSFORMED = 0.3;  // 합화 성립 → 원래 속성 거의 상실
const COMBINE_PENALTY_DEFAULT = 0.7;      // 합이불화 → 약간 약화

const PILLAR_POSITIONS: PillarPosition[] = ['year', 'month', 'day', 'hour'];

/**
 * 득령 점수 (최대 35) — 월지 십이운성 기반.
 *
 * 계절적 지지가 강할수록 높은 점수를 부여한다.
 */
const DEUKRYEONG_SCORE: Record<TwelveStage, number> = {
  '건록': 35,
  '제왕': 35,
  '관대': 28,
  '장생': 25,
  '양': 18,
  '목욕': 15,
  '쇠': 8,
  '태': 5,
  '병': 3,
  '사': 1,
  '묘': 0,
  '절': 0,
};

/**
 * 오행이 일간을 돕는지 확인한다.
 *
 * 비겁(동일 오행) 또는 인성(나를 생하는 오행)이면 지원 관계이다.
 */
function isSupporting(dayMasterElement: Ohaeng, targetElement: Ohaeng): boolean {
  if (dayMasterElement === targetElement) return true;
  const generatingMap: Record<Ohaeng, Ohaeng> = {
    '木': '水', '火': '木', '土': '火', '金': '土', '水': '金',
  };
  return generatingMap[dayMasterElement] === targetElement;
}

/**
 * 신강신약 분석.
 *
 * 점수 기반 채점 체계 (최대 ~90점):
 * - 득령 (최대 35): 월지의 계절적 지원
 * - 통근 (최대 25): 지장간에 일간 오행이 뿌리내린 정도
 *   음양 동일 100%, 음양 상이 70%, 인성 50%
 * - 투출 (최대 15): 천간에 지원 오행이 드러난 정도
 * - 득세 (최대 15): 원국 전체 구성의 지원 비율
 *   천간 3개 + 지지 본기 4개 = 7개 원소
 *
 * 9단계: 극약(≤10), 태약(11-20), 신약(21-30), 중화신약(31-38),
 *         중화(39-45), 중화신강(46-55), 신강(56-70), 태강(71-85), 극왕(>85)
 */
export function analyzeDayMasterStrength(
  fourPillars: FourPillars,
  noHour?: boolean,
  relations?: Relation[],
  relationOptions?: RelationOptions,
): DayMasterStrength {
  const dayMaster = fourPillars.day.stem;
  const dayMasterElement = getStemElement(dayMaster);
  const positions: readonly PillarPosition[] = noHour ? ['year', 'month', 'day'] : PILLAR_POSITIONS;

  // 1. 득령 (max 35) - seasonal support from month branch
  const monthStage = getTwelveStage(dayMaster, fourPillars.month.branch);
  const deukryeong = DEUKRYEONG_SCORE[monthStage.stage];

  // 2. 통근 (max 25) - roots in hidden stems
  // 음양까지 같으면 100%, 음양 다르면 70%, 인성이면 50%
  // 충/합 약화: 충 당한 지지는 통근력 대폭 감소, 합 된 지지는 통근력 약화
  const dayMasterEumyang = getStemEumyang(dayMaster);
  const inseongElement = getElementRelation(dayMasterElement).generatedBy;

  const positionPenalty = buildTonggeunPenaltyMap(fourPillars, relations, relationOptions);

  let tonggeun = 0;
  for (const pos of positions) {
    const relationPenalty = positionPenalty.get(pos) ?? 1.0;
    const hiddenWithRatio = getHiddenStemsWithRatio(fourPillars[pos].branch);
    for (const { stem, ratio } of hiddenWithRatio) {
      const el = getStemElement(stem);
      // 본기(ratio>=16): 8점, 중기(ratio>=7): 5점, 여기: 2점
      let baseScore: number;
      if (ratio >= 16) baseScore = 8;
      else if (ratio >= 7) baseScore = 5;
      else baseScore = 2;

      if (el === dayMasterElement) {
        // 동일 오행: 음양까지 같으면 100%, 다르면 70%
        const yy = getStemEumyang(stem);
        const weight = yy === dayMasterEumyang ? 1.0 : 0.7;
        tonggeun += baseScore * weight * relationPenalty;
      } else if (el === inseongElement) {
        // 인성 (나를 생해주는 오행): 50%
        tonggeun += baseScore * 0.5 * relationPenalty;
      }
    }
  }
  tonggeun = Math.round(Math.min(tonggeun, 25) * 10) / 10;

  // 3. 투출 (max 15) - supporting stems visible in other pillars
  let tuchul = 0;
  for (const pos of positions) {
    if (pos === 'day') continue;
    const stemEl = getStemElement(fourPillars[pos].stem);
    if (stemEl === dayMasterElement) {
      tuchul += 5;
    } else if (isSupporting(dayMasterElement, stemEl)) {
      tuchul += 3;
    }
  }
  tuchul = Math.min(tuchul, 15);

  // 4. 득세 (max 15) - overall chart composition support (ratio-based)
  // Count supporting vs restraining among elements:
  // other heavenly stems + branch main hidden stems
  let supportCount = 0;
  for (const pos of positions) {
    if (pos !== 'day') {
      const stemEl = getStemElement(fourPillars[pos].stem);
      if (isSupporting(dayMasterElement, stemEl)) supportCount++;
    }
    const hiddenWithRatio = getHiddenStemsWithRatio(fourPillars[pos].branch);
    const mainStemEl = getStemElement(hiddenWithRatio[hiddenWithRatio.length - 1].stem);
    if (isSupporting(dayMasterElement, mainStemEl)) supportCount++;
  }
  // Gradient scoring: 사주의 2/5 범주만 일간을 지원하므로
  // 40%가 자연적 균형점. 비율에 따라 0~15점 배분.
  // noHour: 천간 2(년/월) + 지지본기 3(년/월/일) = 5, 정상: 천간 3 + 지지본기 4 = 7
  const totalElements = noHour ? 5 : 7;
  const deukse = Math.min(15, Math.round(supportCount / totalElements * 15 / 0.8));

  // Total score
  const score = Math.round((deukryeong + tonggeun + tuchul + deukse) * 10) / 10;

  // Determine level
  const level = getStrengthLevel(score);

  return {
    level,
    score,
    details: {
      deukryeong,
      tonggeun,
      tuchul,
      deukse,
    },
  };
}

/**
 * 충/합에 의한 통근 약화 패널티 맵을 구축한다.
 *
 * 육충에 관여한 지지: 통근력 대폭 감소 (辰戌丑未는 부분 유지)
 * 육합에 관여한 지지: 합화 성립 시 거의 상실, 합이불화 시 약간 약화
 */
function buildTonggeunPenaltyMap(
  fourPillars: FourPillars,
  relations?: Relation[],
  relationOptions?: RelationOptions,
): Map<PillarPosition, number> {
  const penaltyMap = new Map<PillarPosition, number>();
  if (!relations) return penaltyMap;

  const applyClash = relationOptions?.applyClashWeakening !== false;
  const applyCombine = relationOptions?.applyCombineWeakening !== false;

  for (const r of relations) {
    if (applyClash && r.type === '육충') {
      for (const p of r.pillars) {
        const branch = fourPillars[p].branch;
        const penalty = EARTH_BRANCHES.includes(branch) ? CLASH_PENALTY_EARTH : CLASH_PENALTY;
        const current = penaltyMap.get(p) ?? 1.0;
        penaltyMap.set(p, Math.min(current, penalty));
      }
    }
    if (applyCombine && r.type === '육합') {
      for (const p of r.pillars) {
        const penalty = r.transformed ? COMBINE_PENALTY_TRANSFORMED : COMBINE_PENALTY_DEFAULT;
        const current = penaltyMap.get(p) ?? 1.0;
        penaltyMap.set(p, Math.min(current, penalty));
      }
    }
  }

  return penaltyMap;
}

/**
 * 총점을 9단계 신강신약 분류로 변환한다.
 */
function getStrengthLevel(score: number): StrengthLevel {
  if (score <= 10) return '극약';
  if (score <= 20) return '태약';
  if (score <= 30) return '신약';
  if (score <= 38) return '중화신약';
  if (score <= 45) return '중화';
  if (score <= 55) return '중화신강';
  if (score <= 70) return '신강';
  if (score <= 85) return '태강';
  return '극왕';
}
